/**
 * RewardCurve — the run's mean reward per optimizer step in the training
 * history dialect: raw step rewards as a thin olive line, the exporter's
 * EMA in ink, an optional dashed baseline reference, and one vertical
 * hairline per run intervention. The current step carries a dot and a
 * mono annotation so the live edge of the run is findable without
 * hovering.
 */

import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import type { TrainingRunDocument, TrainingRewardPoint } from '@/api/generated/model'
import { CHART_INK, CHART_REFERENCE_DASH, CHART_REFERENCE_STROKE } from '@/components/evals/chartStyle'

function legendEntries(run: TrainingRunDocument): { mark: React.ReactNode; label: string }[] {
  const entries = [
    {
      mark: <span className="h-px w-4" style={{ backgroundColor: CHART_INK }} />,
      label: 'Step reward',
    },
    {
      mark: <span className="h-[2px] w-4 bg-foreground" />,
      label: 'Smoothed',
    },
  ]
  if (run.interventions.length > 0) {
    entries.push({
      mark: <span className="h-3 w-px bg-foreground/70" />,
      label: 'Intervention',
    })
  }
  return entries
}

function CurveTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: readonly { payload?: unknown }[]
}): React.JSX.Element | null {
  const point = payload?.[0]?.payload as TrainingRewardPoint | undefined
  if (!active || !point) return null
  return (
    <div className="border border-border bg-card p-2.5 text-xs">
      <p className="font-mono uppercase tracking-wider text-muted-foreground">
        Step {point.step.toLocaleString()}
      </p>
      <p className="mt-1">
        Step reward:{' '}
        <span className="font-mono tabular-nums">{point.reward.toFixed(2)}</span>
      </p>
      <p className="mt-1">
        Smoothed:{' '}
        <span className="font-mono tabular-nums">{point.smoothed.toFixed(2)}</span>
      </p>
    </div>
  )
}

export function RewardCurve({
  run,
  points,
}: {
  run: TrainingRunDocument
  points: TrainingRewardPoint[]
}): React.JSX.Element {
  const latest = points[points.length - 1]
  const quarter = Math.max(1, Math.round(run.total_steps / 4))
  const ticks = [0, quarter, quarter * 2, quarter * 3, run.total_steps]
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        {legendEntries(run).map((entry) => (
          <span
            key={entry.label}
            className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
          >
            {entry.mark}
            {entry.label}
          </span>
        ))}
      </div>
      <div className="h-[320px] w-full">
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <LineChart
            data={points}
            margin={{ top: 24, right: 16, bottom: 8, left: 0 }}
          >
            <CartesianGrid stroke="hsl(var(--border) / 0.6)" />
            <XAxis
              dataKey="step"
              type="number"
              domain={[0, run.total_steps]}
              ticks={ticks}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={(value: number) => value.toLocaleString()}
            />
            <YAxis
              domain={[0, 1]}
              ticks={[0, 0.25, 0.5, 0.75, 1]}
              tickLine={false}
              axisLine={false}
              width={44}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={(value: number) => value.toFixed(2)}
            />
            <Tooltip
              content={({ active, payload }) => (
                <CurveTooltip active={active} payload={payload} />
              )}
              cursor={{
                stroke: 'hsl(var(--muted-foreground) / 0.4)',
                strokeDasharray: '3 3',
              }}
            />
            {run.baseline !== null && (
              <ReferenceLine
                y={run.baseline}
                stroke={CHART_REFERENCE_STROKE}
                strokeDasharray={CHART_REFERENCE_DASH}
                label={{
                  value: `Baseline ${run.baseline.toFixed(2)}`,
                  position: 'insideBottomLeft',
                  fontSize: 10,
                  fill: 'hsl(var(--muted-foreground))',
                }}
              />
            )}
            {run.interventions.map((intervention) => (
              <ReferenceLine
                key={`${intervention.step.toString()}-${intervention.label}`}
                x={intervention.step}
                stroke="hsl(var(--foreground) / 0.7)"
                strokeWidth={1}
                label={{
                  value: intervention.label,
                  position: 'insideTopLeft',
                  fontSize: 10,
                  fill: 'hsl(var(--muted-foreground))',
                }}
              />
            ))}
            <Line
              dataKey="reward"
              stroke={CHART_INK}
              strokeWidth={1}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              dataKey="smoothed"
              stroke="hsl(var(--foreground))"
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
            />
            {latest !== undefined && (
              <ReferenceDot
                x={latest.step}
                y={latest.smoothed}
                r={3.5}
                fill="hsl(var(--foreground))"
                stroke="none"
                label={{
                  value: `Step ${latest.step.toLocaleString()} · ${latest.smoothed.toFixed(2)}`,
                  position: 'top',
                  fontSize: 10,
                  fill: 'hsl(var(--foreground))',
                }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
